<template>
  <div class="developer-page">
    <page-header
      title="Developer Profile"
    >
      <template #action>
        <v-btn class="mr-4" color="blue" dark @click="openNotesModal">
          HR NOTES
        </v-btn>
        <v-btn color="button" @click="goBack">
          BACK
        </v-btn>
      </template>
      <template #breadcrumbs>
        <router-link :to="{ name: 'hr-projects' }">
          List of projects
        </router-link>
        <router-link :to="{ name: 'hr-project-show', params: { id: projectId } }">
          / Project
        </router-link>
        <router-link :to="{ name: 'hr-recruitment-show',
          params: { projectId: projectId, recruitmentId: recruitmentId } }"
        >
          / Recruitment
        </router-link>
        <span>
          / {{ developer.personal_data.first_name }} {{ developer.personal_data.last_name }}
        </span>
      </template>
    </page-header>

    <PersonalInformationsCard />

    <ProfileProgressCard :progress="developer.profile_progress" />

    <section class="developer-data">
      <v-row>
        <v-col cols="12" lg="6">
          <AvailabilityCard />

          <FeaturesCard
            :featureGroups="featureGroups[0]"
            primary
            @edit="openFeatureEditModal"
          />

          <FeaturesCard
            :featureGroups="featureGroups[1]"
            @edit="openFeatureEditModal"
          />

          <FinanceCard />

          <RecommendationCard :score="score" />
        </v-col>

        <v-col cols="12" lg="6">
          <PersonalityCard
            :skills="developer.soft_skills"
            :token="developer.membership_token"
            @invitate="generateInvitationLink"
          />

          <FeaturesCard
            :featureGroups="featureGroups[2]"
            @edit="openFeatureEditModal"
          />

          <EducationCard :education="developer.education" />

          <DescriptionCard :description="(developer.description || '')" />

          <AttachmentsCard
            :attachments="developer.attachments"
            :isDeveloperProfile="true"
            title="Attachments"
            @delete="deleteAttachment"
            @edit="openFileModal"
            @visibility-toggle="changeAttachmentVisibility"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col class="text-right">
          <v-btn
            color="red"
            large
            class="developer-data__delete-btn"
            :loading="isProfileSubmitProcessing"
            :disabled="isProfileSubmitProcessing"
            @click="deleteProfile"
          >
            Delete profile
          </v-btn>
          <v-btn
            color="blue"
            large
            class="developer-data__cv-btn"
            @click="openCvCreateModal"
          >
            Generate CV (.pdf)
          </v-btn>
        </v-col>
      </v-row>
    </section>

    <FeaturesModal
      v-model="isFeatureEditModalVisible"
      :selected-feature="selectedFeatureType"
      :features-data="featuresData"
      :processing="isSubmitProcessing"
      @submit="submitFeatures"
    />

    <LanguagesModal
      v-model="isLanguagesModalVisible"
      :features-data="featuresData"
      :languages-data="developer.languages"
      @submit="submitLanguage"
    />

    <NotesModal v-model="isNotesModalVisible" />

    <BasicDataModal
      v-model="isBasicDataModalVisible"
      :features-data="featuresData"
      :processing="isSubmitProcessing"
    />

    <CvCreateModal
      v-model="isCvCreateModalVisible"
      :developer-score="score"
      @success="cvGenerated"
    />

    <AttachmentsModal
      v-model="isFileModalVisible"
      :attachments="developer.attachments"
      :isSubmitProcessing="isAttachmentProcessing"
      @addAttachments="addAttachments"
    />

    <ConfirmDialog ref="confirmDialog" />
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import PageHeader from '@/components/PageHeader.vue';
import BasicDataModal from '@/components/Developers/Show/BasicDataModal.vue';
import FeaturesModal from '@/components/Developers/Show/FeaturesModal.vue';
import NotesModal from '@/components/Developers/Show/NotesModal.vue';
import LanguagesModal from '@/components/Developers/Show/LanguagesModal.vue';
import DescriptionCard from '@/components/Developers/Show/DescriptionCard.vue';
import ProfileProgressCard from '@/components/Developers/Show/ProfileProgressCard.vue';
import FinanceCard from '@/components/Developers/Show/FinanceCard.vue';
import FeaturesCard from '@/components/Developers/Show/FeaturesCard.vue';
import PersonalityCard from '@/components/Developers/Show/PersonalityCard.vue';
import EducationCard from '@/components/Developers/Show/EducationCard.vue';
import PersonalInformationsCard from '@/components/Developers/Show/PersonalInformationsCard.vue';
import AvailabilityCard from '@/components/Developers/Show/AvailabilityCard.vue';
import AttachmentsCard from '@/components/AttachmentsCard.vue';
import CvCreateModal from '@/components/Developers/Show/CvCreateModal.vue';
import RecommendationCard from '@/components/Developers/Show/RecommendationCard.vue';
import ConfirmDialog from '@/components/ConfirmDialog.vue';
import AttachmentsModal from '@/components/AttachmentsModal.vue';


export default {
  metaInfo() {
    return {
      title: `Developer - ${this.developer.personal_data.last_name} ${this.developer.personal_data.first_name}`,
    };
  },

  components: {
    PageHeader,
    BasicDataModal,
    PersonalInformationsCard,
    ProfileProgressCard,
    FinanceCard,
    FeaturesCard,
    PersonalityCard,
    EducationCard,
    DescriptionCard,
    AvailabilityCard,
    FeaturesModal,
    NotesModal,
    LanguagesModal,
    AttachmentsCard,
    CvCreateModal,
    RecommendationCard,
    ConfirmDialog,
    AttachmentsModal,
  },

  props: {
    developerId: {
      type: [String, Number],
      required: true,
    },
    projectId: {
      type: [String, Number],
      required: true,
    },
    recruitmentId: {
      type: [String, Number],
      required: true,
    },
  },

  data() {
    return {
      selectedFeatureType: {},
      isFeatureEditModalVisible: false,
      isLanguagesModalVisible: false,
      isBasicDataModalVisible: false,
      isNotesModalVisible: false,
      isCvCreateModalVisible: false,
      isFileModalVisible: false,
    };
  },

  computed: {
    ...mapState('hrDeveloper', [
      'developer',
      'score',
      'isSubmitProcessing',
      'isProfileSubmitProcessing',
    ]),

    ...mapState('attachment', ['isAttachmentProcessing']),

    ...mapState('features', [
      'featuresData',
    ]),

    featureGroups() {
      return [
        [
          {
            name: 'Primary Roles',
            slug: 'roles',
            data: this.developer.roles,
            primary: true,
          },
          {
            name: 'Top Skills',
            slug: 'top_skills',
            data: this.developer.top_skills,
            primary: true,
          },
          {
            name: 'Skills',
            slug: 'skills',
            data: this.developer.skills,
            primary: true,
          },
          {
            name: 'Languages',
            slug: 'languages',
            data: this.developer.languages,
            primary: true,
          },
        ],
        [
          {
            name: 'Tools experience',
            slug: 'tools',
            data: this.developer.tools,
          },
          {
            name: 'Management methodologies experience',
            slug: 'management',
            data: this.developer.management,
          },
        ],
        [
          {
            name: 'Technology preferences',
            slug: 'technologies',
            data: this.developer.technologies,
          },
          {
            name: 'Project areas preferences',
            slug: 'project_areas',
            data: this.developer.project_areas,
          },
          {
            name: 'Hobbies',
            slug: 'hobbies',
            data: this.developer.hobbies,
          },
        ],
      ];
    },
  },

  mounted() {
    this.fetchDeveloper(this.developerId)
      .then(() => {
        this.isBasicDataModalVisible = !this.developer.basic_data_filled;
      });

    const recruitmentData = {
      recruitmentId: this.recruitmentId,
      developerId: this.developerId,
    };
    this.fetchDeveloperScore(recruitmentData);
  },

  methods: {
    ...mapActions('hrDeveloper', [
      'fetchDeveloper',
      'fetchDeveloperScore',
      'updateDeveloper',
      'deleteDeveloper',
      'generateDeveloperCv',
      'generateDeveloperInvitation',
    ]),

    ...mapActions('attachment', [
      'uploadAttachments',
      'deleteAttachments',
      'updateAttachment',
    ]),

    deleteProfile() {
      this.$refs.confirmDialog
        .open({
          message: 'Are you sure you want to delete profile?',
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
        })
        .then((confirm) => {
          if (confirm) {
            this.deleteDeveloper(this.developerId)
              .then(() => {
                this.$router.push({ name: 'hr-recruitment-show', params: { projectId: this.projectId, recruitmentId: this.recruitmentId } });
              });
          }
        });
    },

    goBack() {
      this.$router.push({ name: 'hr-recruitment-show', params: { projectId: this.projectId, recruitmentId: this.recruitmentId } });
    },

    openFeatureEditModal(featureType) {
      if (featureType.slug === 'languages') {
        this.isLanguagesModalVisible = true;
      } else {
        this.selectedFeatureType = featureType;
        this.isFeatureEditModalVisible = true;
      }
    },

    openNotesModal() {
      this.isNotesModalVisible = true;
    },

    openCvCreateModal() {
      this.isCvCreateModalVisible = true;
    },

    submitLanguage(languageData) {
      this.updateDeveloper({ languages: languageData })
        .then(() => {
          this.isLanguagesModalVisible = false;
        });
    },

    submitFeatures(newFeaturesData) {
      const newData = {};
      newData[this.selectedFeatureType.slug] = newFeaturesData;

      this.updateDeveloper(newData)
        .then(() => {
          this.isFeatureEditModalVisible = false;
        });
    },

    addAttachments(files) {
      const attachmentData = {
        attachments: files,
        apiType: 'hr',
        id: this.developerId,
        recordType: 'developer',
      };

      this.uploadAttachments(attachmentData)
        .then(() => {
          this.isFileModalVisible = false;
          this.fetchDeveloper(this.developerId);
        });
    },

    deleteAttachment(attachmentId) {
      const attachmentData = {
        id: attachmentId,
        apiType: 'hr',
      };

      this.$refs.confirmDialog
        .open({
          message: 'Are you sure you want to delete?',
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
        })
        .then((confirm) => {
          if (confirm) {
            this.deleteAttachments(attachmentData)
              .then(() => {
                this.fetchDeveloper(this.developerId);
              });
          }
        });
    },

    changeAttachmentVisibility(attachment) {
      const attachmentData = {
        id: attachment.id,
        client_visibility: !attachment.client_visibility,
      };
      this.updateAttachment(attachmentData)
        .finally(() => {
          this.fetchDeveloper(this.developerId);
        });
    },

    generateInvitationLink() {
      const invitation = {
        first_name: this.developer.personal_data.first_name,
        last_name: this.developer.personal_data.last_name,
        email: this.developer.personal_data.email,
      };

      this.generateDeveloperInvitation(invitation)
        .then(() => {
          this.fetchDeveloper(this.developerId);
        });
    },

    cvGenerated() {
      this.isCvCreateModalVisible = false;
      this.fetchDeveloper(this.developerId);
    },

    openFileModal() {
      this.isFileModalVisible = true;
    },
  },
};
</script>

<style lang="scss" scoped>
.developer-data {
  margin-bottom: 0px;

  &__delete-btn {
    min-width: 27% !important;
    color: white;
    margin-right: 24px;
  }

  &__cv-btn {
    min-width: 27% !important;
    color: white;
  }
}
</style>
